import { Component, Inject, Input } from '@angular/core';

import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { MandatoryMap } from '../../../shared/helpers/mandatory-map.class';

import { PerimetreAntenne } from '../entities/perimetre-antenne.entity';
import { PerimetreParc } from '../entities/perimetre-parc.entity';

import { AntenneService } from '../../structure/antenne/services/antenne.service';
import { ParcService } from '../../structure/parc/services/parc.service';
import { SousParcService } from '../../structure/sous-parc/services/sous-parc.service';

type AntenneDisplay = {
	nom: string,
	parcsComplets: string[],
	parcsIncomplets: ParcDisplay[],
};

type ParcDisplay = {
	code: string,
	ouvrages: string[],
	sousParcsComplets: string[],
	sousParcsIncomplets: SousParcDisplay[],
};

type SousParcDisplay = {
	code: string,
	ouvrages: string[],
};

@Component({
	selector: 'perimetre',
	templateUrl: 'perimetre.html',
	styleUrls: ['perimetre.css'],
})
export class PerimetreComponent {
	private antennes$ = new BehaviorSubject<PerimetreAntenne[]>([]);
	
	antennesCompletes: string[] = [];
	antennesIncompletes: AntenneDisplay[] = [];
	
	constructor(
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(SousParcService) sousParcService: SousParcService,
	) {
		// Pre-chargement sur les donnnes stucturelles
		let antennesMap$ = antenneService.getAntenneTLRMapById().pipe(
			map(
				(antennesMap) => new MandatoryMap(antennesMap, "Antenne")
			)
		);
		let parcsMap$ = parcService.getParcTLRMapById().pipe(
			map(
				(parcsMap) => new MandatoryMap(parcsMap, "Parc")
			)
		);
		let sousParcsMap$ = sousParcService.getSousParcTLRMapById().pipe(
			map(
				(sousParcsMap) => new MandatoryMap(sousParcsMap, "SousParc")
			)
		);
		
		// Preparation des donnees apres reception d'un nouveau set de perimetres d'antennes
		let preparedData$ = combineLatest([this.antennes$, antennesMap$, parcsMap$, sousParcsMap$]).pipe(
			map(
				([antennes, antennesMap, parcsMap, sousParcsMap]) => {
					let antennesCompletes: string[] = [];
					let antennesIncompletes: AntenneDisplay[] = [];
					
					for (let antenne of antennes) {
						let antenneTLR = antennesMap.get(antenne.antenneId);
						let nomAntenne = antenneTLR.nom;
						if (antenne.complete) {
							antennesCompletes.push(nomAntenne);
						} else  {
							let antenneDisplay = {
								nom: nomAntenne,
								parcsComplets: [],
								parcsIncomplets: [],
							} as AntenneDisplay;
							
							if (antenne.parcs) {
								for (let parc of antenne.parcs) {
									let parcTLR = parcsMap.get(parc.parcId);
									let codeParc = parcTLR.code;
									if (parc.complete) {
										antenneDisplay.parcsComplets.push(codeParc);
									} else {
										let parcDisplay = {
											code: codeParc,
											ouvrages: [],
											sousParcsComplets: [],
											sousParcsIncomplets: [],
										} as ParcDisplay;
										
										if (parc.ouvrages) {
											parcDisplay.ouvrages = parc.ouvrages.map(ouvrage => ouvrage.nom);
											parcDisplay.ouvrages.sort();
										}
										
										if (parc.sousParcs) {
											for (let sousParc of parc.sousParcs) {
												let sousParcTLR = sousParcsMap.get(sousParc.sousParcId);
												let codeSousParc = sousParcTLR.code;
												if (sousParc.complete) {
													parcDisplay.sousParcsComplets.push(codeSousParc);
												} else {
													let sousParcDisplay = {
														code: codeSousParc,
														ouvrages: [],
													} as SousParcDisplay;
													
													if (sousParc.ouvrages) {
														sousParcDisplay.ouvrages = sousParc.ouvrages.map(ouvrage => ouvrage.nom);
														sousParcDisplay.ouvrages.sort();
													}
													
													parcDisplay.sousParcsIncomplets.push(sousParcDisplay);												
												}
											}
											parcDisplay.sousParcsComplets.sort();
											parcDisplay.sousParcsIncomplets.sort((spi1, spi2) => spi1.code.localeCompare(spi2.code));
										}
										
										antenneDisplay.parcsIncomplets.push(parcDisplay);
									}
								}
								antenneDisplay.parcsComplets.sort();
								antenneDisplay.parcsIncomplets.sort((pi1, pi2) => pi1.code.localeCompare(pi2.code));
							};
							
							antennesIncompletes.push(antenneDisplay);
						}
					}
					
					antennesCompletes.sort();
					antennesIncompletes.sort((ai1, ai2) => ai1.nom.localeCompare(ai2.nom));
				
					return {
						antennesCompletes: antennesCompletes,
						antennesIncompletes: antennesIncompletes,
					}	
				}
			)
		);
		
		// Binding des informations sur la vue
		preparedData$.subscribe(
			({antennesCompletes, antennesIncompletes}) => {
				this.antennesCompletes = antennesCompletes;
				this.antennesIncompletes = antennesIncompletes;
			}
		)
	}
	
	@Input() set antennes(value: PerimetreAntenne[]) {
		if (value != undefined) {
			this.antennes$.next(value);
		}
	};
}